import type { EditorialWorkflowStatusId } from '@/components/reach/editorial/editorial-status-ids';
import type { EditorialArticleScore } from '@/hooks/reach/useEditorialArticleTrail';
import type { ArticleApprovalState } from '@/lib/content-doctrine/article-progress';

export const EDITORIAL_NEXT_STEP_OPTIONS = [
  { value: 'all', label: 'All next steps' },
  { value: 'write', label: 'Write' },
  { value: 'running', label: 'Run in progress' },
  { value: 'run-failed', label: 'Run failed' },
  { value: 'check', label: 'Check or re-check' },
  { value: 'check-claims', label: 'Check claims' },
  { value: 'resolve', label: 'Resolve findings' },
  { value: 'send', label: 'Send for approval' },
  { value: 'waiting', label: 'Waiting on client' },
  { value: 'changes', label: 'Changes asked' },
  { value: 'hand-off', label: 'Hand off' },
  { value: 'seo-check', label: 'SEO and internal check' },
  { value: 'add-images', label: 'Add images' },
  { value: 'awaiting', label: 'Awaiting publish' },
  { value: 'done', label: 'Done' },
] as const;

export type EditorialNextStepId = (typeof EDITORIAL_NEXT_STEP_OPTIONS)[number]['value'];

export function editorialNextStep(input: {
  statusId: EditorialWorkflowStatusId;
  hasArticle: boolean;
  /** Only whether the draft checks are current is read, so the article header can pass its release input. */
  score?: {
    reviewState: EditorialArticleScore['reviewState'];
    webReadiness?: { isCurrent: boolean } | null;
  } | null;
  approval?: ArticleApprovalState | null;
}): Exclude<EditorialNextStepId, 'all'> {
  const { statusId, hasArticle, score, approval } = input;
  if (statusId === 'in-progress') return 'running';
  if (!hasArticle) return 'write';
  if (statusId === 'run-failed') return 'run-failed';
  if (statusId === 'published') return 'done';
  if (statusId === 'handed-off' || statusId === 'seo-review') return 'seo-check';
  if (statusId === 'images') return 'add-images';
  if (statusId === 'scheduled') return 'awaiting';
  if (!score) return 'check';
  if (score.reviewState === 'review-limited') return 'check';
  if (score.reviewState === 'claims-pending') return 'check-claims';
  if (score.reviewState === 'needs-review') {
    return !score.webReadiness || !score.webReadiness.isCurrent ? 'check' : 'resolve';
  }
  if (approval === 'approved') return 'hand-off';
  if (approval === 'waiting') return 'waiting';
  if (approval === 'changes') return 'changes';
  return 'send';
}
